import { TranslateService } from '@ngx-translate/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import { cloneDeep } from 'lodash';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { RecipientsInstanceToken, RecipientsInterface } from '../../interfaces/recipients.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { CompositeModel } from './composite.model';
import { NotificationModel } from '../low-level/notification.model';
import { ODataRecipientOutModel, RecipientModel, RecipientModelFromJson } from '../low-level/recipient.model';
import { RadioModelOptions } from '../options/radio-model-options';
import { MultilineTextModel, ODataTextLineOutModel } from '../primitive/multiline-text.model';
import { JsonTextModel, TextModel } from '../primitive/text.model';
import { ODataConfirmationFollowupOutModel } from './confirmation-followup.model';
import { RadioModel } from './radio.model';
import { JsonRecipientListModel, RecipientListModel } from './recipient-list.model';

export class ConfirmationServiceReportModel extends CompositeModel {
    protected readonly configService: ConfigInterface;
    private readonly recipientsService: RecipientsInterface;

    public existingRecipients: RecipientListModel;
    public customerPurchaseOrderNumber: TextModel | undefined;
    public serviceReportLanguage: TextModel;
    public commCategory: string;
    public cseEmailLongText: MultilineTextModel;
    public reportVisibilityToCustomer: RadioModel<TextModel> | undefined;
    public readonly crmLongTextNOSRSet: MultilineTextModel;
    public constructor(public notificationModel: NotificationModel) {
        super('ConfirmationServiceReportModel', {});

        const injector = CustomInjector.getInjector();
        const translate = injector.get(TranslateService);
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        this.configService = injector.get(ConfigInstanceToken);

        this.recipientsService = injector.get(RecipientsInstanceToken);

        this.commCategory = ConfigConstants.confirmationServiceReportCommCategory;

        this.customerPurchaseOrderNumber = <TextModel>(
            this.set(new TextModel('CustomerPO', notificationModel.customerPurchaseOrderNr))
        );
        modelOptionsLoader.load(this.customerPurchaseOrderNumber, 'ServiceReport', 'AKZ');

        this.serviceReportLanguage = new TextModel('Language', notificationModel.languageId);

        // For new data maintenance enhancement, get Recipients from local storage
        if (this.configService.getShowDataMaintenanceEnhancement()) {
            this.updateRecipientEntriesFromLocalStorage();
        } else {
            this.updateRecipientEntries();
        }

        this.crmLongTextNOSRSet = <MultilineTextModel>this.set(new MultilineTextModel('CrmLongTextNOSRSet', ''));
        if (this.configService.getShowTpfReportVisibility()) {
            const optionGroup: Array<DisplayDecorator<TextModel>> = [];
            optionGroup.push(
                new DisplayDecorator(
                    <string>translate.instant('CONFIRMATION_SERVICE_REPORT_YES_LABEL'),
                    new TextModel(ConfigConstants.optionYes, ConfigConstants.optionValueYes),
                ),
            );
            optionGroup.push(
                new DisplayDecorator(
                    <string>translate.instant('CONFIRMATION_SERVICE_REPORT_NO_LABEL'),
                    new TextModel(ConfigConstants.optionNo, ConfigConstants.optionValueNo),
                ),
            );
            optionGroup.push(
                new DisplayDecorator(
                    <string>translate.instant('CONFIRMATION_SERVICE_REPORT_NA_LABEL'),
                    new TextModel(ConfigConstants.optionNA, ConfigConstants.optionValueNA),
                ),
            );
            const two = 2;
            const invalidSelection = this.configService.getMandatoryTpfReportVisibility() ? two : -1;
            const defaultSelection = this.configService.getDefaultTpfReportVisibility();
            this.reportVisibilityToCustomer = new RadioModel<TextModel>(
                'ReportVisibilityToCustomer',
                optionGroup,
                new RadioModelOptions({ initialSelection: defaultSelection, invalidSelection: invalidSelection }),
            );
        }
    }

    private setRecipients(recipientEntries: RecipientModel[]): void {
        this.existingRecipients = <RecipientListModel>(
            this.set(new RecipientListModel('CrmEmailFaxSet', recipientEntries))
        );
    }

    private updateCseEmailRecipient(recipientEntries: RecipientModel[]): void {
        const cseEmailFax = this.configService.getCseEmailFax();
        if (cseEmailFax) {
            // Add CSE Email in text box
            recipientEntries.push(
                new RecipientModel(cseEmailFax, this.commCategory, true, true, false, false, false, true),
            );
        }
    }

    /**
     * If ShowDataMaintenanceEnhancement is enabled, then we have to check if data present in local storage from data Maintenance screen
     * If ShowDataMaintenanceEnhancement is not enabled, then check if any recipient stored earlier in local storage from confirmation screen
     * else update recipients from NDP EmailSet
     *
     * @param recipients
     * @protected
     */
    protected updateRecipientEntries(recipients: RecipientModel[] = []): void {
        if (recipients.length > 0) {
            const uniqueRecipients =
                recipients.filter(
                    (recipient, index, array) =>
                        array.findIndex(
                            (r) => r.faxEmail.value.toLowerCase() === recipient.faxEmail.value.toLowerCase(),
                        ) === index,
                ) ?? [];
            this.setRecipients(uniqueRecipients);
        } else {
            this.updateRecipientsEntriesFromEmailSet();
        }
    }

    /**
     * If a recipient is saved locally from data maintenance screen then, it also contains emails from Ndp -> emailSet
     * If not saved, then update from Emails set
     *
     * @protected
     */
    protected updateRecipientEntriesFromLocalStorage(): void {
        const storedRecipients = cloneDeep(
            this.recipientsService.getRecipientsByNotificationId(this.notificationModel.identifier),
        );
        const combinedUniqueRecipients =
            storedRecipients?.filter(
                (recipient, index, array) =>
                    array.findIndex(
                        (r) => r.faxEmail.value.toLowerCase() === recipient.faxEmail.value.toLowerCase(),
                    ) === index,
            ) ?? [];
        if (combinedUniqueRecipients !== undefined && combinedUniqueRecipients.length > 0) {
            this.recipientsService.saveRecipientsByNotificationId(
                combinedUniqueRecipients,
                this.notificationModel.identifier,
            );
            this.setRecipients(combinedUniqueRecipients);
        } else {
            this.updateRecipientsEntriesFromEmailSet();
        }
    }

    /**
     * Updated Recipients Entries from Ndp->EmailSet
     *
     * @protected
     */
    private updateRecipientsEntriesFromEmailSet(): void {
        const recipientEntries: RecipientModel[] = [];
        const maxEntries = this.configService.getMaxRecipientEntries();

        for (let i = 0; i < maxEntries; ++i) {
            if (this.notificationModel.emailSet[i] && this.notificationModel.emailSet[i].faxEmail.value) {
                // Add all email recipient as readonly
                recipientEntries.push(
                    new RecipientModel(
                        this.notificationModel.emailSet[i].faxEmail.value,
                        this.commCategory,
                        true,
                        false,
                        false,
                        false,
                        true,
                        true,
                    ),
                );
            }
        }
        this.updateCseEmailRecipient(recipientEntries);
        this.setRecipients(recipientEntries);
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     */
    public toJSON(): unknown {
        return {
            existingRecipients: this.existingRecipients,
            customerPurchaseOrderNumber: this.customerPurchaseOrderNumber?.value,
            serviceReportLanguage: this.serviceReportLanguage.value,
            reportVisibilityToCustomer: this.reportVisibilityToCustomer?.getSelectedValue(),
        };
    }

    public getOutModel(): ODataConfirmationServiceReportOutModel {
        const outModel = <ODataConfirmationServiceReportOutModel>super.getOutModel();
        if (this.crmLongTextNOSRSet.value !== ConfigConstants.sapTrue) {
            delete (outModel as Omit<ODataConfirmationFollowupOutModel, 'CrmLongTextNOSRSet'>).CrmLongTextNOSRSet;
        }
        return outModel;
    }
}

export class ConfirmationServiceReportModelFromJson extends ConfirmationServiceReportModel {
    public constructor(
        data: JsonConfirmationServiceReportModel | JsonConfirmationServiceReportOldModel,
        notificationModel: NotificationModel,
    ) {
        super(notificationModel);
        if (!this.customerPurchaseOrderNumber) {
            throw new Error(
                'Could not access customerPurchoseOrderNumber on confirmation service report model from JSON',
            );
        }

        this.customerPurchaseOrderNumber.value =
            (typeof data.customerPurchaseOrderNumber === 'object' // migration logic for introducing toJson
                ? data.customerPurchaseOrderNumber.value
                : data.customerPurchaseOrderNumber) || '';
        // migration logic for introducing toJson starts
        if (typeof data.serviceReportLanguage === 'object') {
            this.serviceReportLanguage.value = data.serviceReportLanguage.value;
            // migration logic for introducing toJson ends
        } else {
            this.serviceReportLanguage.value = data.serviceReportLanguage;
        }
        const existingRecipients = data.existingRecipients.value.map(
            (jsonRecipients) => new RecipientModelFromJson(jsonRecipients),
        );
        if (!this.configService.getShowDataMaintenanceEnhancement()) {
            this.updateRecipientEntries(existingRecipients);
        } else {
            this.existingRecipients.value = existingRecipients;
        }
        if (data.reportVisibilityToCustomer && this.reportVisibilityToCustomer) {
            this.reportVisibilityToCustomer.selectByPredicate(
                (entry) => entry.value === data.reportVisibilityToCustomer,
            );
            this.crmLongTextNOSRSet.value =
                this.reportVisibilityToCustomer.getSelectedValue() === ConfigConstants.optionValueNo
                    ? ConfigConstants.sapTrue
                    : '';
        }
    }
}

export interface JsonConfirmationServiceReportOldModel {
    existingRecipients: JsonRecipientListModel;
    customerPurchaseOrderNumber: JsonTextModel;
    serviceReportLanguage: JsonTextModel;
    reportVisibilityToCustomer: string | undefined;
}

export interface JsonConfirmationServiceReportModel {
    existingRecipients: JsonRecipientListModel;
    customerPurchaseOrderNumber: string | undefined;
    serviceReportLanguage: string;
    reportVisibilityToCustomer: string | undefined;
}

export interface ODataConfirmationServiceReportOutModel extends ODataObject {
    CustomerPO: string;
    CrmEmailFaxSet: ODataRecipientOutModel[];
    CrmLongTextCSEESet: ODataTextLineOutModel[];
    CrmLongTextNOSRSet: ODataTextLineOutModel[]; // For P58 countries
}
